/**
 * Session bootstrap: `/launch#lt=<token>` -> POST <api>/v1/session/launch -> StoredSession.
 * The token lives only in the fragment (never a query string) and is removed from the URL as
 * soon as it has been read. A 403 at first login is retried once after 2 s (design: "Could not
 * sign the phone in — retrying"), then the re-launch card is shown.
 */
import { z } from 'zod';
import { LaunchResponse, type SessionRole } from '@ccphone/contracts';
import { ApiClient, ApiError } from './api.js';
import type { StoredSession } from './session-store.js';

export const LAUNCH_RETRY_MS = 2000;

/** `#lt=abc` or `#lt=abc&x=y` -> `abc`; null when absent */
export function parseLaunchFragment(hash: string): string | null {
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of h.split('&')) {
    const [k, v] = part.split('=');
    if (k === 'lt' && v) {
      try {
        return decodeURIComponent(v);
      } catch {
        return v;
      }
    }
  }
  return null;
}

export const LaunchUrl = z.string().url();

/** the token out of a full launch URL (`https://phone.x/launch#lt=...`) */
export function launchTokenFromUrl(url: string): string | null {
  try {
    return parseLaunchFragment(new URL(url).hash);
  } catch {
    return null;
  }
}

const UUID_RE = /^[0-9a-f-]{36}$/i;

/** stable per-browser id for push subscriptions and session rows */
export function browserId(storage: Pick<Storage, 'getItem' | 'setItem'> | null, random: () => string = () => crypto.randomUUID()): string {
  try {
    const existing = storage?.getItem('ccphone.browser_id');
    if (existing && UUID_RE.test(existing)) return existing;
    const id = random();
    storage?.setItem('ccphone.browser_id', id);
    return id;
  } catch {
    return random();
  }
}

export type LaunchFailure = 'forbidden' | 'network' | 'invalid' | 'maintenance' | 'not_found';

export class LaunchError extends Error {
  constructor(
    readonly kind: LaunchFailure,
    message: string,
  ) {
    super(message);
    this.name = 'LaunchError';
  }
}

function classify(e: unknown): LaunchError {
  if (e instanceof ApiError) {
    if (e.status === 403 || e.status === 401) return new LaunchError('forbidden', e.message);
    if (e.status === 503) return new LaunchError('maintenance', e.message);
    if (e.status === 404 || e.status === 410) return new LaunchError('not_found', e.message);
    if (e.code === 'invalid_response') return new LaunchError('invalid', e.message);
    return new LaunchError('network', e.message);
  }
  return new LaunchError('network', e instanceof Error ? e.message : 'launch failed');
}

export interface ExchangeOptions {
  api: ApiClient;
  lt: string;
  browserId: string;
  role?: SessionRole;
  /** injected for tests */
  sleep?: (ms: number) => Promise<void>;
  onRetry?: (err: LaunchError) => void;
}

/** POST /v1/session/launch with the single 403 retry; returns the parsed LaunchResponse */
export async function exchangeLaunchToken(o: ExchangeOptions): Promise<LaunchResponse> {
  const body = { lt: o.lt, browser_id: o.browserId, role: o.role ?? 'phone' };
  try {
    return await o.api.request('POST', '/v1/session/launch', LaunchResponse, body, { auth: false });
  } catch (e) {
    const err = classify(e);
    if (err.kind !== 'forbidden' && err.kind !== 'network') throw err;
    o.onRetry?.(err);
    await (o.sleep ?? ((ms) => new Promise<void>((r) => setTimeout(r, ms))))(LAUNCH_RETRY_MS);
    try {
      return await o.api.request('POST', '/v1/session/launch', LaunchResponse, body, { auth: false });
    } catch (e2) {
      throw classify(e2);
    }
  }
}

export function toStoredSession(launch: LaunchResponse, browser: string, clockOffsetMs: number, now: () => number = Date.now): StoredSession {
  return { launch, stored_at: new Date(now()).toISOString(), browser_id: browser, clock_offset_ms: clockOffsetMs };
}

/** server-relative expiry check with a safety margin: a stored session is only reused while its JWT is alive */
export function storedSessionUsable(s: StoredSession, serverNowMs: number, marginMs = 60_000): boolean {
  const exp = Date.parse(s.launch.expires_at);
  const cap = Date.parse(s.launch.hard_cap_at);
  if (Number.isNaN(exp) || Number.isNaN(cap)) return false;
  return serverNowMs + marginMs < exp && serverNowMs + marginMs < cap;
}
